import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Save, LogOut, Loader2, CheckCircle2, Inbox } from "lucide-react";

const Field = ({ label, value, onChange, textarea, type = "text", testId }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-muted">{label}</span>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid={testId}
        className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-3 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50"
      />
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        data-testid={testId}
        className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2.5 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold/50"
      />
    )}
  </label>
);

export default function AdminConfig() {
  const [token] = useState(() => getAdminToken());

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const auth = useCallback((tok) => ({ headers: { Authorization: `Bearer ${tok}` } }), []);

  const loadConfig = useCallback(
    async (tok) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`${API}/admin/trail-config`, auth(tok));
        setConfig(data);
      } catch (e) {
        if (e.response?.status === 401) {
          adminLogout();
        } else {
          setError("Could not load the configuration.");
        }
      } finally {
        setLoading(false);
      }
    },
    [auth]
  );

  useEffect(() => {
    if (token) loadConfig(token);
  }, [token, loadConfig]);

  const logout = () => adminLogout();

  const updateCheckin = (i, key, val) =>
    setConfig((c) => ({
      ...c,
      checkin_states: c.checkin_states.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)),
    }));

  const updatePause = (i, key, val) =>
    setConfig((c) => ({
      ...c,
      pauses: c.pauses.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)),
    }));

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await axios.put(`${API}/admin/trail-config`, { config }, auth(token));
      setSaved(true);
      setTimeout(() => setSaved(false), 2600);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
      } else {
        setError(e.response?.data?.detail || "Could not save changes.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen bg-ivory-100 pb-28" data-testid="admin-editor">
      <main className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        <AdminNav onLogout={logout} />
        <h1 className="mb-6 font-display text-3xl text-ink">Trail Config — check-in & pause prompts</h1>
        {loading && <p className="py-10 text-center text-ink-muted">Loading configuration…</p>}
        {error && <p className="mb-4 rounded-lg bg-terracotta-light/20 px-4 py-2 text-sm text-terracotta-deep">{error}</p>}

        {config && (
          <>
            <section className="mb-10">
              <h2 className="mb-1 font-display text-lg text-ink">Terrain check-in states</h2>
              <p className="mb-4 text-sm text-ink-muted">
                The options a reader taps under “Reading the terrain”, with the adaptation shown back to them.
              </p>
              <div className="space-y-5">
                {config.checkin_states.map((s, i) => (
                  <div
                    key={s.id || i}
                    data-testid={`admin-checkin-${s.id || i}`}
                    className="rounded-xl bg-ivory-50 p-4 ring-1 ring-ivory-400/50"
                  >
                    <p className="mb-3 text-[11px] uppercase tracking-[0.14em] text-gold-deep">#{s.id}</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Label" value={s.label} onChange={(v) => updateCheckin(i, "label", v)} testId={`admin-checkin-label-${s.id}`} />
                      <Field label="Contour (elevation)" type="number" value={s.contour} onChange={(v) => updateCheckin(i, "contour", v)} />
                    </div>
                    <div className="mt-3 grid gap-3">
                      <Field label="Description" value={s.description} onChange={(v) => updateCheckin(i, "description", v)} textarea />
                      <Field label="Adaptation shown to reader" value={s.adaptation} onChange={(v) => updateCheckin(i, "adaptation", v)} textarea testId={`admin-checkin-adaptation-${s.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-1 font-display text-lg text-ink">Pause practices</h2>
              <p className="mb-4 text-sm text-ink-muted">Grounding pauses offered at waypoints.</p>
              <div className="space-y-5">
                {config.pauses.map((p, i) => (
                  <div
                    key={p.id || i}
                    data-testid={`admin-pause-${p.id || i}`}
                    className="rounded-xl bg-ivory-50 p-4 ring-1 ring-ivory-400/50"
                  >
                    <p className="mb-3 text-[11px] uppercase tracking-[0.14em] text-gold-deep">#{p.id}</p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field label="Title" value={p.title} onChange={(v) => updatePause(i, "title", v)} testId={`admin-pause-title-${p.id}`} />
                      <Field label="Seconds" type="number" value={p.seconds} onChange={(v) => updatePause(i, "seconds", v)} />
                    </div>
                    <div className="mt-3">
                      <Field label="Prompt text" value={p.text} onChange={(v) => updatePause(i, "text", v)} textarea testId={`admin-pause-text-${p.id}`} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </main>

      {config && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-ivory-400/50 bg-ivory-100/95 px-5 py-4 backdrop-blur sm:px-10">
          <div className="mx-auto flex max-w-3xl items-center justify-end gap-3">
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600" data-testid="admin-saved">
                <CheckCircle2 className="h-4 w-4" /> Saved
              </span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="admin-save-btn"
              className="debossed-btn inline-flex items-center gap-2 rounded-full px-6 py-3 font-display text-ink disabled:opacity-40"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
